import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FileText, Plus, Edit, Trash2, Eye, Copy } from 'lucide-react';
import { templatesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';
import EmptyState from '../../components/UI/EmptyState';

const AdminTemplatesPage = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const queryClient = useQueryClient();

  // Fetch templates
  const { data: templatesData, isLoading, error } = useQuery(
    ['admin-templates', selectedCategory],
    () => templatesAPI.getAll({ 
      category: selectedCategory === 'all' ? undefined : selectedCategory,
      limit: 100 
    }),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Fetch categories
  const { data: categoriesData } = useQuery(
    'template-categories',
    () => templatesAPI.getCategories(),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Handle different possible response structures
  const templates = templatesData?.data?.data?.templates || templatesData?.data?.templates || [];
  const categories = categoriesData?.data?.data?.categories || categoriesData?.data?.categories || [];

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => templatesAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-templates');
        toast.success('Template deleted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete template');
      },
    }
  );

  const handleDelete = (template) => {
    if (window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      deleteMutation.mutate(template.id);
    }
  };

  const handleDuplicate = (template) => {
    setEditingTemplate({
      ...template,
      id: null,
      name: `${template.name} (Copy)`,
      isActive: false
    });
    setShowCreateModal(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Templates"
        subtitle="Manage announcement templates and categories."
        actions={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                queryClient.invalidateQueries(['admin-templates']);
                queryClient.invalidateQueries('template-categories');
              }}
            >
              Refresh
            </Button>
            <Button variant="primary" icon={Plus} onClick={() => setShowCreateModal(true)}>
              New Template
            </Button>
          </>
        }
      />

      {/* Category Filter */}
      <Card padded={false}>
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex space-x-1">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                selectedCategory === 'all'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All ({templates.length})
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  selectedCategory === category
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Templates List */}
        <div className="px-4 py-5 sm:p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No templates found"
              subtitle="Get started by creating your first template."
              actionLabel="Create Template"
              actionIcon={Plus}
              onAction={() => setShowCreateModal(true)}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <Card key={template.id} interactive>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {template.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {template.category}
                      </p>
                      <p className="text-xs text-gray-400 mt-2 line-clamp-3">
                        {(template.template_text || template.templateText || '').substring(0, 100)}...
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <StatusBadge
                        label={(template.is_active ?? template.isActive) ? 'Active' : 'Inactive'}
                        tone={(template.is_active ?? template.isActive) ? 'success' : 'neutral'}
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      {template.placeholders?.length || 0} placeholders
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => {
                          setEditingTemplate(template);
                          setShowCreateModal(true);
                        }}
                        className="p-1 text-gray-400 hover:text-primary-600"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(template)}
                        className="p-1 text-gray-400 hover:text-blue-600"
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(template)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
          onSave={() => {
            queryClient.invalidateQueries('admin-templates');
            queryClient.invalidateQueries('template-categories');
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
};

// Template Create/Edit Modal Component
const TemplateModal = ({ template, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    category: template?.category || 'Airport',
    templateText: template?.template_text || template?.templateText || '',
    description: template?.description || '',
    isActive: template?.is_active ?? template?.isActive ?? true,
  });

  const createMutation = useMutation(
    (data) => templatesAPI.create(data),
    {
      onSuccess: () => {
        toast.success('Template created successfully');
        onSave();
      },
      onError: (error) => {
        console.error('Create template error:', error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to create template';
        toast.error(errorMessage, { duration: 5000 });
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => templatesAPI.update(id, data),
    {
      onSuccess: () => {
        toast.success('Template updated successfully');
        onSave();
      },
      onError: (error) => {
        console.error('Update template error:', error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to update template';
        toast.error(errorMessage, { duration: 5000 });
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Template name must be at least 2 characters');
      return;
    }
    
    if (!formData.category || formData.category.trim().length < 2) {
      toast.error('Please select a category');
      return;
    }
    
    if (!formData.templateText || formData.templateText.trim().length < 5) {
      toast.error('Template text must be at least 5 characters');
      return;
    }
    
    if (formData.templateText.trim().length > 2000) {
      toast.error('Template text cannot exceed 2000 characters');
      return;
    }
    
    if (formData.description && formData.description.length > 1000) {
      toast.error('Description cannot exceed 1000 characters');
      return;
    }
    
    // Submit the form
    if (template?.id) {
      updateMutation.mutate({ id: template.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const extractPlaceholders = (text) => {
    const matches = text.match(/\{([^}]+)\}/g);
    // A placeholder repeated in the text (e.g. {NewGate} twice) is one value to
    // fill in — list each name once, in order of first appearance.
    return matches ? [...new Set(matches.map(m => m.slice(1, -1)))] : [];
  };

  const placeholders = extractPlaceholders(formData.templateText);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {template?.id ? 'Edit Template' : 'Create New Template'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Template Name</label>
                <input
                  type="text"
                  required
                  className="input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter template name"
                />
              </div>
              
              <div>
                <label className="label">Category</label>
                <select
                  className="input"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="Airport">Airport</option>
                  <option value="School">School</option>
                  <option value="Hospital">Hospital</option>
                  <option value="Retail">Retail</option>
                  <option value="Transportation">Transportation</option>
                  <option value="Emergency">Emergency</option>
                  <option value="General">General</option>
                </select>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center">
                <label className="label">Template Text</label>
                <span className={`text-xs ${formData.templateText.length < 5 ? 'text-red-500' : formData.templateText.length > 2000 ? 'text-red-500' : 'text-gray-500'}`}>
                  {formData.templateText.length} / 2000 characters
                  {formData.templateText.length < 5 && ' (min: 5)'}
                </span>
              </div>
              <textarea
                required
                rows={6}
                className={`input ${formData.templateText.length > 0 && formData.templateText.length < 5 ? 'border-red-500' : ''}`}
                value={formData.templateText}
                onChange={(e) => setFormData({ ...formData, templateText: e.target.value })}
                placeholder="Enter template text with placeholders like {FlightNumber}, {Gate}, etc."
              />
              <p className="mt-1 text-xs text-gray-500">
                Use curly braces for placeholders: {"{FlightNumber}, {Gate}, {Destination}"}
              </p>
              {formData.templateText.length > 0 && formData.templateText.length < 5 && (
                <p className="mt-1 text-xs text-red-500">
                  ⚠️ Template text must be at least 5 characters
                </p>
              )}
            </div>

            {placeholders.length > 0 && (
              <div>
                <label className="label">Detected Placeholders</label>
                <div className="flex flex-wrap gap-2">
                  {placeholders.map((placeholder, index) => (
                    <StatusBadge key={index} label={placeholder} tone="info" />
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="label">Description (Optional)</label>
              <textarea
                rows={2}
                className="input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of when to use this template"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active (visible to users)
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={createMutation.isLoading || updateMutation.isLoading}
              >
                {template?.id ? 'Update Template' : 'Create Template'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminTemplatesPage;
